// src/features/datev/components/EmployeeCard.tsx
//
// Strukturierte Darstellung eines DATEV-Mitarbeiterdatensatzes im Stil einer Personalakte.
// Wird im Lohnaustauschdatenservice (hr:exchange) für Lese- und Schreibvorgänge genutzt.
//
// Werteschutz (12.10.2026, Freigabe Tim): IBAN, SV-Nummer, europäische
// SV-Nummer und Steuer-ID erscheinen verdeckt. Mit der Eigenschaft geschuetzt
// (Mitarbeiter aus dem Spiegel) zeigt die Karte die Masken aus der Datenbank
// und holt den Klartext über "Anzeigen" protokolliert vom Server. Ohne sie
// (Daten live von DATEV) wird der vorhandene Wert verdeckt und auf Klick
// eingeblendet. Die vollständige Ansicht zeigt diese Felder immer verdeckt.

import React, { useState } from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import {
  DatevExchangeEmployee,
  DatevExchangeError
} from '../../../services/datev/DatevExchangeService';
import { SchutzFeld, VERDECKT, verdecke } from '../werteSchutz';

// ============================================
// DEUTSCHE ÜBERSETZUNGEN FÜR DATEV-FEHLERCODES
// ============================================
// Mapping bekannter DATEV-Fehlercodes auf deutschsprachige Beschreibungen.
// Unbekannte Codes fallen auf die original DATEV-Beschreibung zurück.

const DATEV_ERROR_TRANSLATIONS: Record<string, string> = {
  '#DCO02426': 'Validierungsfehler — mindestens ein Datenwert konnte nicht übernommen werden',
  '#DCO02403': 'Ein oder mehrere Mitarbeiterdatensätze konnten nicht angelegt werden',
  '#DCO02420': 'Der übermittelte Wert überschreitet die zulässige Stellenanzahl',
  '#DXLO02423': 'Der referenzierte Wert existiert im Mandantenstamm nicht (z.B. Lohnart oder Betriebsstätte nicht angelegt)',
  '#DXLO02424': 'Pflichtfeld fehlt oder ist leer',
  '#DXLO02425': 'Ungültiger Wertebereich für dieses Feld',
  'restapi.ValidationError': 'Validierungsfehler bei der API-Verarbeitung',
  'restapi.ValidatePropertyExistsForClient': 'Der referenzierte Wert existiert im Mandantenstamm nicht',
  'JOB_FAILED': 'Der DATEV-Job ist fehlgeschlagen',
  'POLL_TIMEOUT': 'Zeitüberschreitung beim Abfragen des Job-Status'
};

const translateError = (code: string | undefined): string | null => {
  if (!code) return null;
  return DATEV_ERROR_TRANSLATIONS[code] || null;
};

// ============================================
// HELPER-FUNKTIONEN FÜR FELDFORMATIERUNG
// ============================================

/** Stellt einen Wert dar; leere/null-Werte als "—" */
const fmt = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  return String(value);
};

/** Formatiert ein YYYY-MM-DD Datum auf deutsch (DD.MM.YYYY) */
const fmtDate = (value: any): string => {
  if (!value) return '—';
  const s = String(value);
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return s;
};

/** Geschlecht-Code in deutsche Bezeichnung */
const fmtSex = (value: any): string => {
  if (value === 'M') return 'Männlich';
  if (value === 'W') return 'Weiblich';
  if (value === 'D') return 'Divers';
  return fmt(value);
};

/** IBAN-Formatierung in 4er-Gruppen */
const fmtIban = (value: any): string => {
  if (!value) return '—';
  return String(value).replace(/(.{4})/g, '$1 ').trim();
};

/** Wochentagsverteilung Mo-So → lesbarer String */
const fmtWeekDistribution = (activity: any): string => {
  if (!activity) return '—';
  const days = [
    { key: 'allocation_of_working_hours_monday', label: 'Mo' },
    { key: 'allocation_of_working_hours_tuesday', label: 'Di' },
    { key: 'allocation_of_working_hours_wednesday', label: 'Mi' },
    { key: 'allocation_of_working_hours_thursday', label: 'Do' },
    { key: 'allocation_of_working_hours_friday', label: 'Fr' },
    { key: 'allocation_of_working_hours_saturday', label: 'Sa' },
    { key: 'allocation_of_working_hours_sunday', label: 'So' }
  ];
  const parts = days
    .filter(d => activity[d.key] !== null && activity[d.key] !== undefined)
    .map(d => `${d.label} ${activity[d.key]}h`);
  return parts.length > 0 ? parts.join(' · ') : '—';
};

/** Technischen DATEV-Feldnamen lesbar machen: Unterstriche zu Leerzeichen,
 *  erster Buchstabe groß. Der technische Name bleibt erkennbar. */
const humanizeKey = (key: string): string => {
  const spaced = String(key).replace(/_/g, ' ').trim();
  if (!spaced) return String(key);
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** true, wenn value ein echtes Objekt ist (kein Array, kein null) */
const isPlainObject = (value: any): boolean =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// ============================================
// STYLE-KONSTANTEN
// ============================================

const sectionStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  padding: '16px 20px',
  marginBottom: '12px'
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: '600',
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  margin: '0 0 12px 0',
  paddingBottom: '8px',
  borderBottom: '1px solid #F3F4F6'
};

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '12px 20px'
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: '2px'
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#111827',
  fontWeight: '500'
};

/** Einzelnes Feld-Label-Paar */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={fieldValueStyle}>{value}</div>
  </div>
);

// ============================================
// VERDECKTER WERT (Werteschutz)
// ============================================

export interface GeschuetzteWerte {
  /** Masken aus dem Spiegel je Feld */
  masken: Partial<Record<SchutzFeld, string | null>>;
  /** Klartext vom Server holen (protokolliert) */
  anzeigen: (feld: SchutzFeld) => Promise<string>;
}

const knopfStyle: React.CSSProperties = {
  marginLeft: '8px', border: 'none', background: 'transparent', color: '#2563EB',
  cursor: 'pointer', fontSize: '12px', padding: 0
};

const SchutzWert: React.FC<{ feld: SchutzFeld; lokal: any; geschuetzt?: GeschuetzteWerte }> = ({ feld, lokal, geschuetzt }) => {
  const [klar, setKlar] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const lokalVorhanden = lokal !== null && lokal !== undefined && lokal !== '';
  const verdeckt = geschuetzt ? (geschuetzt.masken[feld] || null) : (lokalVorhanden ? VERDECKT : null);
  if (!verdeckt) return <>—</>;

  const zeigen = async () => {
    if (!geschuetzt) { setKlar(String(lokal)); return; }
    setLaedt(true);
    setFehler(null);
    try {
      setKlar(await geschuetzt.anzeigen(feld));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Wert konnte nicht angezeigt werden.');
    } finally {
      setLaedt(false);
    }
  };

  const text = klar ? (feld === 'iban' ? fmtIban(klar.replace(/\s+/g, '')) : klar) : verdeckt;
  return (
    <span>
      <span style={feld === 'iban' ? { fontFamily: 'Menlo, Monaco, monospace' } : undefined}>{text}</span>
      {klar
        ? <button style={knopfStyle} onClick={() => setKlar(null)}>Verbergen</button>
        : <button style={knopfStyle} onClick={zeigen} disabled={laedt}>{laedt ? 'Wird geladen …' : 'Anzeigen'}</button>}
      {fehler && <span style={{ marginLeft: '8px', color: '#B91C1C', fontSize: '12px' }}>{fehler}</span>}
    </span>
  );
};

// ============================================
// REKURSIVE VOLLDARSTELLUNG EINES BELIEBIGEN WERTES
// ============================================
// Zeigt jeden Wert des Datensatzes an, egal ob Primitivwert, Objekt oder
// Liste. Verschachtelte Objekte werden eingerückt gruppiert, Listen je
// Eintrag aufgeschlüsselt. So bleibt die Anzeige vollständig und muss bei
// neuen DATEV-Feldern nie nachgepflegt werden.

const RawValue: React.FC<{ label: string; value: any }> = ({ label, value }) => {
  // Verschachteltes Objekt
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
          {label}
        </div>
        <div style={{ borderLeft: '2px solid #E5E7EB', paddingLeft: '12px' }}>
          {keys.length === 0
            ? <div style={fieldValueStyle}>—</div>
            : keys.map(k => <RawValue key={k} label={humanizeKey(k)} value={value[k]} />)}
        </div>
      </div>
    );
  }

  // Liste
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div style={{ marginTop: '6px' }}>
          <span style={fieldLabelStyle}>{label}: </span>
          <span style={fieldValueStyle}>—</span>
        </div>
      );
    }
    const allPrimitive = value.every(v => !isPlainObject(v) && !Array.isArray(v));
    if (allPrimitive) {
      return (
        <div style={{ marginTop: '6px' }}>
          <span style={fieldLabelStyle}>{label}: </span>
          <span style={fieldValueStyle}>{value.map(v => fmt(v)).join(', ')}</span>
        </div>
      );
    }
    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
          {label} ({value.length})
        </div>
        <div style={{ borderLeft: '2px solid #E5E7EB', paddingLeft: '12px' }}>
          {value.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', fontFamily: 'Menlo, Monaco, monospace' }}>
                #{idx + 1}
              </div>
              {isPlainObject(item)
                ? Object.keys(item).map(k => <RawValue key={k} label={humanizeKey(k)} value={item[k]} />)
                : <div style={fieldValueStyle}>{fmt(item)}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Primitivwert
  return (
    <div style={{ marginTop: '6px' }}>
      <span style={fieldLabelStyle}>{label}: </span>
      <span style={{ ...fieldValueStyle, fontFamily: 'Menlo, Monaco, monospace' }}>{fmt(value)}</span>
    </div>
  );
};

// ============================================
// HAUPT-KOMPONENTE: PERSONALAKTE
// ============================================

interface EmployeeCardProps {
  employee: DatevExchangeEmployee;
  /** Optional: Header-Hinweis (z.B. "Neu angelegt") */
  badge?: string;
  /** Optional: Hinweis unter dem Header (z.B. Sandbox-Hinweis) */
  footnote?: string;
  /** Optional: Masken und Anzeige vom Server (Mitarbeiter aus dem Spiegel) */
  geschuetzt?: GeschuetzteWerte;
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({ employee, badge, footnote, geschuetzt }) => {
  const emp = employee || {} as DatevExchangeEmployee;
  const pd = emp.personal_data || {};
  const addr = emp.address || {};
  const act = emp.activity || {};
  const acc = emp.account || {};
  const tax = emp.tax_card || {};
  const taxa = emp.taxation || {};
  const si = emp.social_insurance || {};
  const vac = emp.vacation_entitlement || {};
  const voc = emp.vocational_training || {};

  // employment_periods kann Objekt ODER Array sein (DATEV-Inkonsistenz)
  const periodsArr = Array.isArray(emp.employment_periods)
    ? emp.employment_periods
    : emp.employment_periods ? [emp.employment_periods] : [];
  const latestPeriod = periodsArr[periodsArr.length - 1] || {};

  // Alle Top-Level-Schlüssel des Datensatzes für die vollständige Ansicht,
  // geschützte Werte darin verdeckt
  const allKeys = Object.keys(emp as Record<string, any>).sort();
  const empVerdeckt = verdecke(emp) as Record<string, any>;

  return (
    <div>
      {/* Kopfbereich: Name + Personalnummer prominent */}
      <div style={{
        backgroundColor: '#1E40AF',
        color: 'white',
        borderRadius: '8px 8px 0 0',
        padding: '20px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Personalstammdaten
            </div>
            <div style={{ fontSize: '22px', fontWeight: '600' }}>
              {fmt(emp.first_name)} {fmt(emp.surname)}
            </div>
            {pd.academic_title && (
              <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '2px' }}>
                {pd.academic_title}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Personalnummer
            </div>
            <div style={{ fontSize: '22px', fontWeight: '600', fontFamily: 'Menlo, Monaco, monospace' }}>
              {fmt(emp.personnel_number)}
            </div>
            {emp.company_personnel_number && (
              <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                Firmen-PersNr: {emp.company_personnel_number}
              </div>
            )}
          </div>
        </div>

        {badge && (
          <div style={{
            marginTop: '12px',
            display: 'inline-block',
            backgroundColor: '#10B981',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            ✓ {badge}
          </div>
        )}

        {footnote && (
          <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.85, fontStyle: 'italic' }}>
            {footnote}
          </div>
        )}
      </div>

      {/* Persönliche Daten */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Persönliche Daten</h4>
        <div style={fieldGridStyle}>
          <Field label="Geschlecht" value={fmtSex(pd.sex)} />
          <Field label="Geburtsdatum" value={fmtDate(pd.date_of_birth)} />
          <Field label="Geburtsort" value={fmt(pd.place_of_birth)} />
          <Field label="Geburtsland (Schlüssel)" value={fmt(pd.country_of_birth)} />
          <Field label="Geburtsname" value={fmt(pd.birth_name)} />
          <Field label="Namenszusatz Geburt" value={fmt(pd.birth_name_affix)} />
          <Field label="Nationalität (Schlüssel)" value={fmt(pd.nationality)} />
          <Field label="E-Mail" value={fmt(pd.email)} />
          <Field label="Telefon" value={fmt(pd.phone)} />
          <Field label="Akademischer Titel" value={fmt(pd.academic_title)} />
          <Field label="Namenspräfix" value={fmt(pd.name_prefix)} />
          <Field label="Namenszusatz" value={fmt(pd.name_affix)} />
          <Field label="Sozialversicherungsnr." value={<SchutzWert feld="sv_nummer" lokal={pd.social_security_number} geschuetzt={geschuetzt} />} />
          <Field label="Europ. SV-Nr." value={<SchutzWert feld="sv_nummer_eu" lokal={pd.european_social_security_number} geschuetzt={geschuetzt} />} />
          <Field label="Arbeitserlaubnis gültig bis" value={fmtDate(pd.work_permit)} />
          <Field label="Aufenthaltserlaubnis gültig bis" value={fmtDate(pd.residency_permit)} />
          <Field label="Studienbescheinigung bis" value={fmtDate(pd.certificate_of_study)} />
        </div>
      </div>

      {/* Adresse */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Adresse</h4>
        <div style={fieldGridStyle}>
          <Field label="Straße" value={fmt(addr.street)} />
          <Field label="Hausnummer" value={fmt(addr.house_number)} />
          <Field label="Adresszusatz" value={fmt(addr.address_affix)} />
          <Field label="PLZ" value={fmt(addr.postal_code)} />
          <Field label="Stadt" value={fmt(addr.city)} />
          <Field label="Land (Schlüssel)" value={fmt(addr.country)} />
        </div>
      </div>

      {/* Beschäftigung */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Beschäftigung</h4>
        <div style={fieldGridStyle}>
          <Field label="Eintrittsdatum" value={fmtDate(latestPeriod.date_of_commencement_of_employment)} />
          <Field label="Austrittsdatum" value={fmtDate(latestPeriod.date_of_termination_of_employment)} />
          <Field label="Tätigkeitsbezeichnung" value={fmt(act.occupational_title)} />
          <Field label="Ausgeübte Tätigkeit (Schlüssel)" value={fmt(act.job_carried_out)} />
          <Field label="Mitarbeitertyp" value={fmt(act.employee_type)} />
          <Field label="Vertragsform" value={fmt(act.contractual_structure)} />
          <Field label="Tätigkeitsart" value={fmt(act.activity_type)} />
          <Field label="Höchste Berufsausbildung" value={fmt(act.highest_level_of_professional_training)} />
          <Field label="Höchster Schulabschluss" value={fmt(act.highest_level_of_education)} />
          <Field label="Wochenarbeitsstunden" value={fmt(act.weekly_working_hours)} />
          <Field label="Arbeitszeitverteilung" value={fmtWeekDistribution(act)} />
          <Field label="Abteilungs-ID" value={fmt(act.department_id)} />
          <Field label="Kostenstelle" value={fmt(act.individual_cost_center_id)} />
          <Field label="Betriebsstätte-ID" value={fmt(emp.business_unit_id)} />
          <Field label="Arbeitnehmerüberlassung" value={fmt(act.personnel_leasing)} />
        </div>
        {periodsArr.length > 1 && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
            Hinweis: {periodsArr.length} Beschäftigungsperioden vorhanden — oben wird die jüngste angezeigt.
          </div>
        )}
      </div>

      {/* Bezahlung */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Bezahlung</h4>
        <div style={fieldGridStyle}>
          <Field label="IBAN" value={<SchutzWert feld="iban" lokal={acc.iban} geschuetzt={geschuetzt} />} />
          <Field label="BIC" value={fmt(acc.bic)} />
          <Field label="Abw. Kontoinhaber" value={fmt(acc.differing_account_holder)} />
          <Field label="Zahlungsart (Schlüssel)" value={fmt(emp.payment_method)} />
        </div>

        {/* Festbezüge */}
        {Array.isArray(emp.gross_payments) && emp.gross_payments.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
              Festbezüge
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6B7280', fontWeight: '500' }}>ID</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6B7280', fontWeight: '500' }}>Lohnart</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6B7280', fontWeight: '500' }}>Betrag</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6B7280', fontWeight: '500' }}>Monate</th>
                </tr>
              </thead>
              <tbody>
                {emp.gross_payments.map((gp: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'Menlo, Monaco, monospace', fontSize: '12px' }}>{fmt(gp.id)}</td>
                    <td style={{ padding: '6px 8px' }}>{fmt(gp.salary_type_id)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'Menlo, Monaco, monospace' }}>
                      {gp.amount !== undefined && gp.amount !== null
                        ? Number(gp.amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                        : '—'}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{fmt(gp.payment_months)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Stundenlöhne */}
        {Array.isArray(emp.hourly_wages) && emp.hourly_wages.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
              Stundenlöhne
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6B7280', fontWeight: '500' }}>ID</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6B7280', fontWeight: '500' }}>Betrag/Stunde</th>
                </tr>
              </thead>
              <tbody>
                {emp.hourly_wages.map((hw: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'Menlo, Monaco, monospace', fontSize: '12px' }}>{fmt(hw.id)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'Menlo, Monaco, monospace' }}>
                      {hw.amount !== undefined && hw.amount !== null
                        ? Number(hw.amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Steuer */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Steuer</h4>
        <div style={fieldGridStyle}>
          <Field label="Steuerklasse" value={fmt(tax.tax_class)} />
          <Field label="Konfession" value={fmt(tax.denomination)} />
          <Field label="Konfession Ehepartner" value={fmt(tax.spouses_denomination)} />
          <Field label="Faktor" value={fmt(tax.factor)} />
          <Field label="Jahresfreibetrag" value={fmt(tax.annual_tax_allowance)} />
          <Field label="Monatlicher Freibetrag" value={fmt(tax.monthly_tax_allowance)} />
          <Field label="Kinderfreibeträge" value={fmt(tax.child_tax_allowances)} />
          <Field label="Steuer-Identifikationsnr." value={<SchutzWert feld="steuer_id" lokal={taxa.tax_identification_number} geschuetzt={geschuetzt} />} />
          <Field label="Beschäftigungsart" value={fmt(taxa.employment_type)} />
          <Field label="Pauschalversteuerung" value={fmt(taxa.flat_rate_tax)} />
          <Field label="Beantragter Jahresfreibetrag" value={fmt(taxa.requested_annual_allowance)} />
        </div>
      </div>

      {/* Sozialversicherung */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Sozialversicherung</h4>
        <div style={fieldGridStyle}>
          <Field label="Beitragsgruppe Krankenvers." value={fmt(si.contribution_class_health_insurance)} />
          <Field label="Beitragsgruppe Pflegevers." value={fmt(si.contribution_class_nursing_insurance)} />
          <Field label="Beitragsgruppe Rentenvers." value={fmt(si.contribution_class_pension_insurance)} />
          <Field label="Beitragsgruppe Arbeitslosenv." value={fmt(si.contribution_class_unemployment_insurance)} />
          <Field label="Krankenkassen-Betriebsnr." value={fmt(si.company_number_of_health_insurer)} />
          <Field label="Krankenkassen-ID" value={fmt(si.health_insurance_id)} />
          <Field label="Kinderlosenzuschlag ignoriert" value={fmt(si.is_additional_contribution_to_nursing_insurance_for_childless_ignored)} />
          <Field label="KK für geringf. Beschäftigte" value={fmt(si.health_insurer_for_marginal_employee)} />
        </div>
      </div>

      {/* Ausbildung / Urlaub */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Urlaub & Ausbildung</h4>
        <div style={fieldGridStyle}>
          <Field label="Urlaubsanspruch (Tage)" value={fmt(vac.basic_vacation_entitlement)} />
          <Field label="Ausbildung Beginn" value={fmtDate(voc.start)} />
          <Field label="Ausbildung Ende (geplant)" value={fmtDate(voc.expected_end)} />
          <Field label="Ausbildung Ende (tatsächlich)" value={fmtDate(voc.actual_end)} />
        </div>
      </div>

      {/* Vollständige DATEV-Daten — zeigt jedes Feld des Datensatzes, auch
          alles, was in den kuratierten Abschnitten oben nicht enthalten ist
          (z.B. individual_data, employment_id, alle Beschäftigungsperioden
          und künftige DATEV-Felder). Leere Werte erscheinen als Strich. */}
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Vollständige DATEV-Daten</h4>
        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
          Sämtliche von DATEV gelieferten Felder dieses Datensatzes, unverkürzt. Leere Felder erscheinen als Strich.
        </div>
        {allKeys.length === 0
          ? <div style={fieldValueStyle}>—</div>
          : allKeys.map(k => <RawValue key={k} label={humanizeKey(k)} value={empVerdeckt[k]} />)}
      </div>
    </div>
  );
};

// ============================================
// KOMPONENTE: DATEV-FEHLER STRUKTURIERT
// ============================================

interface DatevErrorPanelProps {
  errors: DatevExchangeError[];
  /** Optional: Überschrift (Default: "DATEV-Validierungsfehler") */
  title?: string;
}

export const DatevErrorPanel: React.FC<DatevErrorPanelProps> = ({ errors, title }) => {
  if (!errors || errors.length === 0) return null;

  return (
    <div style={{
      backgroundColor: '#FEF2F2',
      border: '1px solid #FECACA',
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <AlertCircle size={18} color="#991B1B" />
        <strong style={{ fontSize: '14px', color: '#991B1B' }}>
          {title || 'DATEV-Validierungsfehler'} ({errors.length})
        </strong>
      </div>

      {errors.map((err, idx) => {
        const mainTranslation = translateError(err.error) || translateError(err.error_description);
        return (
          <div
            key={idx}
            style={{
              backgroundColor: 'white',
              border: '1px solid #FEE2E2',
              borderRadius: '6px',
              padding: '12px 14px',
              marginBottom: idx < errors.length - 1 ? '8px' : 0
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: 'Menlo, Monaco, monospace',
                fontSize: '12px',
                backgroundColor: '#FEE2E2',
                color: '#991B1B',
                padding: '2px 6px',
                borderRadius: '3px',
                fontWeight: '600'
              }}>
                {err.error || err.error_description || 'Unbekannter Fehler'}
              </span>
              {err.request_id && (
                <span style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'Menlo, Monaco, monospace' }}>
                  Request-ID: {err.request_id}
                </span>
              )}
            </div>

            {mainTranslation && (
              <div style={{ fontSize: '13px', color: '#111827', marginTop: '6px', lineHeight: '1.5' }}>
                {mainTranslation}
              </div>
            )}

            {!mainTranslation && err.error_description && (
              <div style={{ fontSize: '13px', color: '#111827', marginTop: '6px', lineHeight: '1.5', fontStyle: 'italic' }}>
                {err.error_description}
              </div>
            )}

            {err.error_uri && (
              <div style={{ marginTop: '6px' }}>
                <a
                  href={err.error_uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '12px',
                    color: '#1E40AF',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  DATEV-Hilfe <ExternalLink size={12} />
                </a>
              </div>
            )}

            {err.additional_messages && err.additional_messages.length > 0 && (
              <div style={{
                marginTop: '10px',
                paddingTop: '10px',
                borderTop: '1px solid #F3F4F6'
              }}>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
                  Details:
                </div>
                {err.additional_messages.map((msg, midx) => {
                  const msgTranslation = translateError(msg.id) || translateError(msg.description);
                  return (
                    <div
                      key={midx}
                      style={{
                        fontSize: '12px',
                        color: '#374151',
                        marginBottom: '6px',
                        paddingLeft: '8px',
                        borderLeft: '2px solid #FEE2E2'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: 'Menlo, Monaco, monospace',
                          fontSize: '11px',
                          backgroundColor: '#F3F4F6',
                          padding: '1px 5px',
                          borderRadius: '3px'
                        }}>
                          {msg.id}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          color: msg.severity === 'ERROR' ? '#991B1B' : '#92400E',
                          textTransform: 'uppercase',
                          fontWeight: '600',
                          letterSpacing: '0.5px'
                        }}>
                          {msg.severity}
                        </span>
                      </div>
                      <div style={{ marginTop: '3px' }}>
                        {msgTranslation || msg.description || '—'}
                      </div>
                      {msg.affected_fields && msg.affected_fields.length > 0 && (
                        <div style={{
                          marginTop: '3px',
                          fontSize: '11px',
                          color: '#6B7280',
                          fontFamily: 'Menlo, Monaco, monospace'
                        }}>
                          Feld: {msg.affected_fields.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
